import { CdkDrag, CdkDragHandle } from '@angular/cdk/drag-drop';
import { ChangeDetectionStrategy, Component, HostListener, inject, OnInit, signal } from '@angular/core';
import { isAuthenticated } from '@app/reducers/auth/auth-service.selectors';
import { Store } from '@ngrx/store';
import { AppState } from '@reducers/interfaces';
import { mgcIconCloseRegular } from '@sb/mgc-icons/mgcIcon-close_regular.icon';
import { mgcIconDownRegular } from '@sb/mgc-icons/mgcIcon-down_regular.icon';
import { mgcIconRightRegular } from '@sb/mgc-icons/mgcIcon-right_regular.icon';
import { UserConfigService } from '@sb/shared/user-config';
import { IconButtonComponent } from '@sb/ui/button';
import { IconComponent, IconRegistry } from '@sb/ui/icon';

import { DevToolsChargebeeComponent } from './dev-tools-chargebee.component';
import { DevToolsDataComponent } from './dev-tools-data.component';
import { DevToolsHiringComponent } from './dev-tools-hiring.component';
import { DevToolsRateLimitComponent } from './dev-tools-rate-limit.component';
import { DevToolsSignupOnboardingComponent } from './dev-tools-signup-onboarding.component';

const KONAMI_CODE = [
  'ArrowUp',
  'ArrowUp',
  'ArrowDown',
  'ArrowDown',
  'ArrowLeft',
  'ArrowRight',
  'ArrowLeft',
  'ArrowRight',
  'KeyB',
  'KeyA',
];

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'app-dev-tools',
  template: `
    @if (isOpen()) {
      <div
        class="fixed right-5 bottom-5 z-toast flex max-h-90v w-80 flex-col rounded-lg border border-inverted bg-inverted text-sm text-inverted shadow-2xl"
        cdkDrag
        cdkDragBoundary="body"
        role="dialog"
        aria-label="Developer Tools"
        aria-modal="false"
        tabindex="-1"
      >
        <div
          class="flex shrink-0 cursor-move items-center justify-between border-inverted bg-hover-inverted px-3.5 py-2.5 select-none"
          [class.rounded-t-lg]="!isCollapsed()"
          [class.border-b]="!isCollapsed()"
          [class.rounded-lg]="isCollapsed()"
          cdkDragHandle
        >
          <span class="text-sm font-semibold">🛠️ Dev Tools</span>
          <div class="flex items-center gap-xs">
            <button
              sb-icon-button
              [color]="'transparentInverted'"
              [attr.aria-expanded]="!isCollapsed()"
              [attr.aria-label]="isCollapsed() ? 'Expand Dev Tools' : 'Collapse Dev Tools'"
              (click)="toggleCollapsed()"
            >
              <sb-icon [name]="isCollapsed() ? 'chevron-right' : 'chevron-down'" />
            </button>
            <button sb-icon-button [color]="'transparentInverted'" (click)="close()">
              <sb-icon [name]="'times'" />
              <span class="sr-only">Close</span>
            </button>
          </div>
        </div>
        @if (!isCollapsed()) {
          <div class="flex flex-col gap-4 overflow-y-auto p-3.5">
            <app-dev-tools-chargebee />
            <app-dev-tools-signup-onboarding />
            <app-dev-tools-hiring />
            <app-dev-tools-rate-limit />
            @if (isAuthenticated()) {
              <app-dev-tools-data />
            }
          </div>
        }
      </div>
    }
  `,
  imports: [
    CdkDrag,
    CdkDragHandle,
    IconButtonComponent,
    IconComponent,
    DevToolsChargebeeComponent,
    DevToolsSignupOnboardingComponent,
    DevToolsHiringComponent,
    DevToolsRateLimitComponent,
    DevToolsDataComponent,
  ],
})
export class DevToolsComponent implements OnInit {
  #userConfig = inject(UserConfigService);

  private store = inject(Store<AppState>);

  isOpen = signal(false);
  isCollapsed = signal(false);
  isAuthenticated = this.store.selectSignal(isAuthenticated);

  private konamiIndex = 0;

  constructor() {
    inject(IconRegistry).registerIcons([mgcIconCloseRegular, mgcIconDownRegular, mgcIconRightRegular]);
  }

  ngOnInit(): void {
    if (this.#userConfig.get('devToolsOpen', false)()) {
      this.isOpen.set(true);
    }

    this.isCollapsed.set(this.#userConfig.get('devToolsCollapsed', false)());
  }

  @HostListener('document:keydown', ['$event'])
  onKeyDown(event: KeyboardEvent): void {
    if (event.code === KONAMI_CODE[this.konamiIndex]) {
      this.konamiIndex++;

      if (this.konamiIndex === KONAMI_CODE.length) {
        this.toggle();
        this.konamiIndex = 0;
      }
    } else {
      this.konamiIndex = 0;
    }
  }

  toggle(): void {
    this.isOpen.update((open) => !open);
    this.#userConfig.set('devToolsOpen', this.isOpen());
  }

  toggleCollapsed(): void {
    this.isCollapsed.update((collapsed) => !collapsed);
    this.#userConfig.set('devToolsCollapsed', this.isCollapsed());
  }

  close(): void {
    this.isOpen.set(false);
    this.#userConfig.set('devToolsOpen', false);
  }
}
